import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Server, TestTube, Save, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import type { DatabaseConfig, NewConfigState, ConnectionStatus } from '@/types/database';
import { useDatabaseConnection } from '@/hooks/useDatabaseConnection';

interface DatabaseConfigFormProps {
  newConfig: NewConfigState;
  setNewConfig: React.Dispatch<React.SetStateAction<NewConfigState>>;
  onConfigAdded: (config: DatabaseConfig) => void;
  onResetForm: () => void;
}

export function DatabaseConfigForm({ 
  newConfig, 
  setNewConfig, 
  onConfigAdded, 
  onResetForm 
}: DatabaseConfigFormProps) {
  const { isTestingConnection, connectionStatus, testConnection } = useDatabaseConnection();

  const handleTestConnection = () => {
    const testConfig: DatabaseConfig = {
      id: 'test',
      name: newConfig.name,
      type: newConfig.type,
      config: newConfig.type === 'supabase' 
        ? { url: newConfig.url, anonKey: newConfig.anonKey }
        : { 
            host: newConfig.host, 
            port: newConfig.port, 
            database: newConfig.database, 
            username: newConfig.username, 
            password: newConfig.password 
          },
      isActive: false
    };
    
    testConnection(testConfig);
  };

  const addNewConfig = async () => {
    if (!newConfig.name) {
      toast.error("يرجى إدخال اسم الإعداد");
      return;
    }

    let configData: DatabaseConfig;

    if (newConfig.type === 'supabase') {
      if (!newConfig.url || !newConfig.anonKey) {
        toast.error("يرجى ملء جميع حقول Supabase");
        return;
      }
      configData = {
        id: Date.now().toString(),
        name: newConfig.name,
        type: 'supabase',
        config: {
          url: newConfig.url,
          anonKey: newConfig.anonKey
        },
        isActive: false
      };
    } else {
      if (!newConfig.host || !newConfig.database || !newConfig.username) {
        toast.error("يرجى ملء جميع الحقول المطلوبة");
        return;
      }
      configData = {
        id: Date.now().toString(),
        name: newConfig.name,
        type: newConfig.type,
        config: {
          host: newConfig.host,
          port: newConfig.port,
          database: newConfig.database,
          username: newConfig.username,
          password: newConfig.password
        },
        isActive: false
      };
    }

    const isConnectionValid = await testConnection(configData);
    if (!isConnectionValid) {
      return;
    }

    onConfigAdded(configData);
    onResetForm();
    toast.success("تم إضافة الإعداد بنجاح!");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Server className="h-5 w-5" />
          إضافة قاعدة بيانات جديدة
        </CardTitle>
        <CardDescription>
          أضف إعدادات اتصال جديدة لقاعدة بيانات مختلفة
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="config-name">اسم الإعداد</Label>
          <Input
            id="config-name"
            placeholder="مثال: قاعدة البيانات التجريبية"
            value={newConfig.name}
            onChange={(e) => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
          />
        </div>

        <div>
          <Label htmlFor="config-type">نوع قاعدة البيانات</Label>
          <Select
            value={newConfig.type}
            onValueChange={(value) => 
              setNewConfig(prev => ({ 
                ...prev, 
                type: value as 'supabase' | 'mysql' | 'postgresql', 
                port: value === 'mysql' ? 3306 : value === 'postgresql' ? 5432 : prev.port 
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="supabase">Supabase</SelectItem>
              <SelectItem value="mysql">MySQL (cPanel)</SelectItem>
              <SelectItem value="postgresql">PostgreSQL</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {newConfig.type === 'supabase' ? (
          <>
            <div>
              <Label htmlFor="config-url">رابط قاعدة البيانات</Label>
              <Input
                id="config-url"
                placeholder="https://your-project-ref.supabase.co"
                value={newConfig.url}
                onChange={(e) => setNewConfig(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="config-key">مفتاح API العام (Anon Key)</Label>
              <Input
                id="config-key"
                type="password"
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                value={newConfig.anonKey}
                onChange={(e) => setNewConfig(prev => ({ ...prev, anonKey: e.target.value }))}
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <Label htmlFor="config-host">المضيف (Host)</Label>
              <Input
                id="config-host"
                placeholder="localhost أو IP العنوان"
                value={newConfig.host}
                onChange={(e) => setNewConfig(prev => ({ ...prev, host: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="config-port">المنفذ (Port)</Label>
              <Input
                id="config-port"
                type="number"
                placeholder={newConfig.type === 'mysql' ? "3306" : "5432"}
                value={newConfig.port}
                onChange={(e) => setNewConfig(prev => ({ ...prev, port: parseInt(e.target.value) || (newConfig.type === 'mysql' ? 3306 : 5432) }))}
              />
            </div>

            <div>
              <Label htmlFor="config-database">اسم قاعدة البيانات</Label>
              <Input
                id="config-database"
                placeholder="database_name"
                value={newConfig.database}
                onChange={(e) => setNewConfig(prev => ({ ...prev, database: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="config-username">اسم المستخدم</Label>
              <Input
                id="config-username"
                placeholder="username"
                value={newConfig.username}
                onChange={(e) => setNewConfig(prev => ({ ...prev, username: e.target.value }))}
              />
            </div>

            <div>
              <Label htmlFor="config-password">كلمة المرور</Label>
              <Input
                id="config-password"
                type="password"
                placeholder="password"
                value={newConfig.password}
                onChange={(e) => setNewConfig(prev => ({ ...prev, password: e.target.value }))}
              />
            </div>
          </>
        )}

        {connectionStatus !== 'idle' && (
          <Alert className={connectionStatus === 'success' ? 'border-green-500' : connectionStatus === 'error' ? 'border-red-500' : ''}>
            <div className="flex items-center gap-2">
              {connectionStatus === 'testing' && <TestTube className="h-4 w-4 animate-spin" />}
              {connectionStatus === 'success' && <CheckCircle className="h-4 w-4 text-green-500" />}
              {connectionStatus === 'error' && <AlertCircle className="h-4 w-4 text-red-500" />}
              <AlertDescription>
                {connectionStatus === 'testing' && 'جاري اختبار الاتصال...'}
                {connectionStatus === 'success' && 'تم الاتصال بنجاح!'}
                {connectionStatus === 'error' && 'فشل الاتصال بقاعدة البيانات'}
              </AlertDescription>
            </div>
          </Alert>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleTestConnection}
            disabled={isTestingConnection}
            variant="outline"
          >
            <TestTube className="h-4 w-4 mr-2" />
            اختبار الاتصال
          </Button>
          
          <Button onClick={addNewConfig}>
            <Save className="h-4 w-4 mr-2" />
            حفظ الإعداد
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}